// app/src/lib/forecast/reforecastRequest.js
// Build the preprocessed-question payload for a REFORECAST so the forecast
// server rebuilds the SAME contract instead of re-deriving one from the bare
// question text.
//
// The contract identity (and thus its BigTable row key) is the hash of
// (question, resolution_criteria, resolution_date) — see server/lib/contractHash.js.
// Reforecast used to call genForecastFromServer with preprocessedQuestion=null,
// sending only { question_text }. The forecast server then defaulted
// resolution_criteria to "Not provided" (sooth_forecast_server/builder.py), which
// CHANGED the hash — re-keying the contract on every reforecast, orphaning the
// old contract (deleted revision + emptied schedule) and destroying the
// resolution criteria. Carrying the existing contract's identity fields through
// keeps the hash — and the key — stable, and preserves the criteria.
//
// Background/context is intentionally NOT part of the hash (same question with a
// different background is the same contract), but it is carried through so the
// contract drawer doesn't visibly degrade on a rerun.
//
// The OUTCOME SPACE is carried for the same reason the identity fields are: a multi-value
// contract's space lives on the contract, not in the question text, so omitting it made the
// server re-author the question as binary — and persist it that way.
import { outcomeSpaceOf } from "./outcomeSpace.js";

/**
 * @param {object|null} contract  the active forecast's contract
 *   ({ question, title, resolution_criteria, resolution_date, context },
 *   plus resolution_moment / resolution_zone / resolution_basis when the
 *   server resolved an exact deadline).
 * @param {string} question       the resolved question text for this reforecast.
 * @param {object|null} [outcomeSpaceOverride]  the space to send when the contract itself
 *   no longer carries one — the caller's wider resolution (e.g. the run's history
 *   vintages, via `outcomeSpaceOfRun`). Only consulted when the contract has no space, so
 *   a live space always wins; a multi-value question whose last rerun degraded to binary
 *   is still re-run as a distribution instead of being re-authored from bare text.
 * @returns {object} preprocessed-question payload for genForecastFromServer's
 *   5th arg (mirrors the fresh-forecast path's shape in useForecastSubmit.js).
 */
export function buildReforecastPreprocessed(contract, question, outcomeSpaceOverride = null) {
  const c = contract || {};
  const rd = c.resolution_date;
  // Normalize a date-only value ("YYYY-MM-DD") to an ISO instant like the fresh
  // path does; pass anything else (already-ISO) through untouched. undefined
  // when absent so the server falls back exactly as before.
  const scheduled = rd
    ? String(rd).length === 10
      ? String(rd) + "T00:00:00Z"
      : String(rd)
    : undefined;
  // Same lookup the branch path uses (`outcomeSpaceOf`: nested under `forecast_value` on a
  // newer contract, top-level on an older one, BooleanSpace rejected). The override is the
  // caller's wider resolution and only fills in when the contract carries nothing.
  const outcomeSpace =
    outcomeSpaceOf(c) ||
    // The override goes through the SAME validator rather than being trusted, so a caller
    // cannot route a binary rerun onto the multiway lane by handing over a BooleanSpace.
    outcomeSpaceOf({ outcome_space: outcomeSpaceOverride });
  const pp = {
    question_text: question,
    // Consistent with the space rather than hardcoded — the preprocess panel prints this.
    question_type: outcomeSpace
      ? outcomeSpace.kind === "numeric"
        ? "numeric"
        : "categorical"
      : "binary",
    title: c.title || question,
    resolution_criteria: c.resolution_criteria || "",
    scheduled_resolution_time: scheduled,
    background_info: c.background || undefined,
  };
  // The EXACT deadline, carried separately from the identity day above.
  //
  // `scheduled_resolution_time` has to stay the day-granular spelling: the
  // contract hash is canonTime(resolution_date), so putting the real instant
  // there would re-key the contract and orphan it — the exact failure this
  // module exists to prevent. Without this block the instant was simply lost,
  // and a 20:00Z market close came back as its own midnight.
  //
  // All three or none. `contractFromPreprocessed` derives the displayed day as
  // dayInZone(deadline, zone), so a zone arriving beside a midnight-UTC deadline
  // would resolve to the PREVIOUS day in New York and re-key the contract on its
  // own. The moment is what makes the zone safe to send.
  if (c.resolution_moment) {
    pp.custom_metadata = {
      resolution_moment_utc: c.resolution_moment,
      resolution_zone: c.resolution_zone || "",
      resolution_basis: c.resolution_basis || "",
      // Display only, not identity: keeps "Tokyo Stock Exchange close" on the rerun.
      ...(c.resolution_label ? { resolution_label: c.resolution_label } : {}),
    };
  }
  // Presence is the switch `genForecastFromServer` reads, so binary must OMIT the key.
  if (outcomeSpace) pp.outcome_space = outcomeSpace;
  return pp;
}
